/**
 * GateManagement Use Case
 * 게이트 관리 상태 및 로직
 */
import { useState, useEffect, useCallback } from 'react';
import { Gate, GateFormData, GateType } from '../models/GateManagement';
import { gateManagementRepository } from '../repositories/GateManagementRepository';
import { gateManagementService } from '../services/GateManagementService';

export const useGateManagement = () => {
  const [gates, setGates] = useState<Gate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const [newGate, setNewGate] = useState<GateFormData>({
    name: '',
    location: '',
    type: 'main_entrance' as GateType,
    direction: 'entry',
    alertSettings: {
      sendParentAlert: false,
      countForStudyTime: true,
    },
  });

  /**
   * 게이트 목록 로드
   */
  const loadGates = useCallback(async () => {
    try {
      const data = await gateManagementRepository.getGates();
      setGates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('게이트 로드 실패:', error);
      // 백엔드 API 미구현 시 빈 배열로 설정
      setGates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 게이트 추가
   */
  const handleAddGate = useCallback(async () => {
    const validation = gateManagementService.validateGateForm(newGate.name, newGate.location);
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    try {
      const newGateData = await gateManagementRepository.createGate(newGate);
      setGates((prev) => [...prev, newGateData]);
      setShowAddModal(false);
      setNewGate({
        name: '',
        location: '',
        type: 'main_entrance',
        direction: 'entry',
        alertSettings: {
          sendParentAlert: false,
          countForStudyTime: true,
        },
      });
      alert('게이트가 성공적으로 추가되었습니다.');
    } catch (error) {
      console.error('게이트 추가 실패:', error);
      alert('게이트 추가에 실패했습니다.');
    }
  }, [newGate]);

  /**
   * 게이트 삭제
   */
  const handleDeleteGate = useCallback(async (gateId: string) => {
    if (!window.confirm('정말 이 게이트를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await gateManagementRepository.deleteGate(gateId);
      setGates((prev) => prev.filter((gate) => gate.id !== gateId));
      alert('게이트가 삭제되었습니다.');
    } catch (error) {
      console.error('게이트 삭제 실패:', error);
      alert('게이트 삭제에 실패했습니다.');
    }
  }, []);

  /**
   * 알림 설정 업데이트
   */
  const handleUpdateAlertSettings = useCallback(
    async (gateId: string, sendParentAlert: boolean, countForStudyTime: boolean) => {
      try {
        await gateManagementRepository.updateAlertSettings(gateId, {
          sendParentAlert,
          countForStudyTime,
        });

        setGates((prev) =>
          prev.map((gate) =>
            gate.id === gateId
              ? {
                  ...gate,
                  alertSettings: { sendParentAlert, countForStudyTime },
                }
              : gate
          )
        );

        alert('알림 설정이 업데이트되었습니다.');
      } catch (error) {
        console.error('알림 설정 업데이트 실패:', error);
        alert('알림 설정 업데이트에 실패했습니다.');
      }
    },
    []
  );

  /**
   * 초기 로드 및 주기적 업데이트
   */
  useEffect(() => {
    loadGates();
    const interval = setInterval(loadGates, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [loadGates]);

  return {
    // 상태
    gates,
    loading,
    showAddModal,
    setShowAddModal,
    newGate,
    setNewGate,

    // 액션
    loadGates,
    handleAddGate,
    handleDeleteGate,
    handleUpdateAlertSettings,
  };
};
